import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';
import SaveTreeModal from '../SaveTreeModal';
import LoadTreeModal from '../LoadTreeModal';
import CanvasNameModal from '../CanvasNameModal';
import AlertModal from '../AlertModal';
import { exportTreeToJSON, downloadJSON } from '../../utils/treeExporter';
import { importTreeFromFile } from '../../utils/treeImporter';
import { GLASSMORPHIC_COLORS } from '../../utils/colorPalette';
import { trackEvent } from '../../utils/analytics';
import { useFeatureAccess } from '../../hooks/useQuota';
import { useAuth } from '../../hooks/useAuth';
import { incrementUsage } from '../../utils/firestoreApi';
import { needsCanvasNaming, handleCanvasNaming } from '../../utils/canvasNamingUtils';
import './Toolbar.css';

// --- ICONS ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>;
const ClearIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 22v-5"/><path d="M14 19v-2"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M18 20v-3"/><path d="M2 13h20"/><path d="M20 13V7l-5-5H6a2 2 0 0 0-2 2v9"/><path d="M6 20v-3"/></svg>;
const ZoomInIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3h-6" /></svg>;
const ZoomOutIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" /></svg>;

const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>;
const FolderIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>;

const ToolbarButton: React.FC<{ onClick: () => void; children: React.ReactNode; title: string, className?: string, disabled?: boolean }> = ({ onClick, children, title, className = '', disabled = false }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={`toolbar-button ${className}`}
  >
    {children}
  </button>
);

const HighlightedButton: React.FC<{ onClick: (event: React.MouseEvent<HTMLButtonElement>) => void; children: React.ReactNode; title: string, disabled?: boolean, className?: string }> = ({ onClick, children, title, disabled = false, className = '' }) => (
    <div className={`highlighted-button-container ${className}`}>
        <button
            onClick={onClick}
            title={title}
            disabled={disabled}
            className="highlighted-button"
        >
            {children}
        </button>
    </div>
);

const Toolbar: React.FC = () => {
  const {
    addNote,
    setSelectionMode,
    zoom,
    showToast,
    state: { isSelectionMode, scale, selectedNoteIds, notes, canvasId },
  } = useWhiteboardStore();

  const { user } = useAuth();
  const { canCreateThreadStarter, canCreateTotalNote } = useFeatureAccess();

  const [showSaveTreeModal, setShowSaveTreeModal] = useState(false);
  const [showLoadTreeModal, setShowLoadTreeModal] = useState(false);
  const [showCanvasNameModal, setShowCanvasNameModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success',
    isConfirmation: false,
    onConfirm: undefined as (() => void) | undefined
  });

  const [colorPopoverOpen, setColorPopoverOpen] = useState(false);
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLButtonElement | null>(null);
  
  // Store the pending note color while canvas name modal is open
  const [pendingNoteColor, setPendingNoteColor] = useState<string | null>(null);
  
  // Loading state for note creation
  const [isCreatingNote, setIsCreatingNote] = useState(false);



  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary', isConfirmation = false, onConfirm?: () => void) => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant,
      isConfirmation,
      onConfirm
    });
    setShowAlertModal(true);
  };

  const handleAddNoteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColorPopoverOpen(true);
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorPopoverOpen(false);
    setColorAnchorEl(null);
  };

  const handleColorSelect = async (color: string) => {
    if (!user) {
      addNote(color);
      trackEvent('sticky_note_created', { node_type: 'sticky_note' });
      handleColorClose();
      return;
    }

    // Check if canvas needs naming (no canvas ID)
    if (needsCanvasNaming(canvasId)) {
      // Store the color and show canvas name modal
      setPendingNoteColor(color);
      setShowCanvasNameModal(true);
      handleColorClose();
      return;
    }

    // Use existing canvas ID for saved canvases, or default for unsaved ones
    const effectiveCanvasId = canvasId || 'default';

    // Show loading state while checking quotas
    setIsCreatingNote(true);
    
    try {
      // Pre-check quotas before creating the note
      const canCreateStarter = await canCreateThreadStarter();
      
      if (!canCreateStarter) {
        // Show quota exceeded message
        showAlert(
          'Thread Starter Limit Reached',
          'You\'ve reached your thread starter limit. Please upgrade your plan to create more thread starters.',
          'danger'
        );
        return;
      }

      // Check if user can create a total note (per canvas limit)
      const canCreateTotal = await canCreateTotalNote();
      
      if (!canCreateTotal) {
        // Show quota exceeded message
        showAlert(
          'Canvas Note Limit Reached',
          'You\'ve reached your total notes limit for this canvas (20 notes). Please upgrade your plan or create a new canvas.',
          'danger'
        );
        return;
      }

      // Quotas are good, create the note
      addNote(color);
      trackEvent('sticky_note_created', { node_type: 'sticky_note' });
      handleColorClose();

      // Note: Quota will be incremented when persona is selected, not here
      // This prevents quota increment if user cancels persona selection

    } catch (error) {
      console.error('Error checking quota for note creation:', error);
      // Fallback: allow note creation even if quota check fails
      addNote(color);
      trackEvent('sticky_note_created', { node_type: 'sticky_note' });
      handleColorClose();
    } finally {
      setIsCreatingNote(false);
    }
  };

  const handleToggleSelectionMode = () => {
    setSelectionMode(!isSelectionMode);
  };

  const handleClearCanvas = () => {
    if (notes.length === 0) {
      showToast('Canvas is already empty', 'info');
      return;
    }

    const handleConfirmClear = () => {
      // Clear all notes by resetting to initial state
      const { newCanvas } = useWhiteboardStore.getState();
      newCanvas();
      showToast(`Cleared ${notes.length} notes from canvas`, 'success');
      setShowAlertModal(false);
    };

    showAlert(
      'Clear Canvas',
      `Are you sure you want to clear all ${notes.length} notes from this canvas? This action cannot be undone.`,
      'danger',
      true, // isConfirmation
      handleConfirmClear
    );
  };

  const handleCanvasNameSave = async (canvasName: string): Promise<void> => {
    if (!user || !pendingNoteColor) return;

    // Return a Promise that resolves when the entire process is complete
    return new Promise((resolve, reject) => {
      handleCanvasNaming(
        canvasName,
        user,
        showToast,
        showAlert,
        async (canvasId) => {
          // Canvas saved successfully, now check quotas before creating note
          const effectiveCanvasId = canvasId;

          try {
            // Pre-check quotas before creating the note
            const canCreateStarter = await canCreateThreadStarter();
            const canCreateTotal = await canCreateTotalNote();
            
            if (!canCreateStarter) {
              showToast('You\'ve reached your thread starter limit. Please upgrade your plan to create more thread starters.', 'error');
              setPendingNoteColor(null);
              reject(new Error('Thread starter limit reached'));
              return;
            }
            
            if (!canCreateTotal) {
              showToast('You\'ve reached your total notes limit for this canvas (20 notes). Please upgrade your plan or create a new canvas.', 'error');
              setPendingNoteColor(null);
              reject(new Error('Total notes limit reached'));
              return;
            }

            // Quotas are good, create the note
            addNote(pendingNoteColor);
            trackEvent('sticky_note_created', { node_type: 'sticky_note' });

            // Note: Quota will be incremented when persona is selected, not here
            // This prevents quota increment if user cancels persona selection

            // Clear pending color and resolve the promise
            setPendingNoteColor(null);
            setShowCanvasNameModal(false); // Close the canvas naming modal
            resolve();
          } catch (error) {
            console.error('Error creating note after canvas naming:', error);
            // Fallback: create note anyway
            addNote(pendingNoteColor);
            trackEvent('sticky_note_created', { node_type: 'sticky_note' });
            setPendingNoteColor(null);
            setShowCanvasNameModal(false); // Close the canvas naming modal
            resolve(); // Still resolve since note was created
          }
        },
        (error) => {
          // Canvas save failed, clear pending color and reject
          setPendingNoteColor(null);
          setShowCanvasNameModal(false); // Close the canvas naming modal
          showToast(`Failed to save canvas: ${error}`, 'error');
          reject(new Error(error));
        }
      );
    });
  };

  const handleCanvasNameCancel = () => {
    setShowCanvasNameModal(false);
    setPendingNoteColor(null);
  };

  const handleZoomIn = () => {
    zoom(scale + 0.1);
  };

  const handleZoomOut = () => {
    zoom(scale - 0.1);
  };



  const handleResetView = () => {
    zoom(0.9);
  };

  const handleSaveTree = (filename: string) => {
    // Dispatch saving event to show animation
    window.dispatchEvent(new CustomEvent('whiteboard-saving', { 
      detail: { type: 'local', filename: filename } 
    }));

    const state = useWhiteboardStore.getState().state;
    const jsonContent = exportTreeToJSON(state);
    downloadJSON(jsonContent, filename);
    trackEvent('tree_exported', { export_format: 'json' });
    
    // Dispatch save event for status indicator
    window.dispatchEvent(new CustomEvent('whiteboard-saved', { 
      detail: { type: 'local', filename: filename } 
    }));
  };

  const getCurrentJsonContent = () => {
    const state = useWhiteboardStore.getState().state;
    return exportTreeToJSON(state);
  };

  const handleLoadTree = async (fileContent: string, canvasId?: string, canvasName?: string, description?: string, tags?: string[]) => {
    try {
      const newState = await importTreeFromFile(fileContent, canvasId);
      
      // Manually add the metadata to the state object
      if (canvasName) {
        newState.canvasName = canvasName;
      }
      newState.description = description;
      newState.tags = tags;

      useWhiteboardStore.getState().loadTree(newState);
      trackEvent('tree_imported', { file_type: 'auto_detected' });
    } catch (error) {
      console.error('Failed to load tree:', error);
      showAlert(
        'Load Failed',
        'Failed to load tree: ' + (error instanceof Error ? error.message : 'Unknown error'),
        'danger'
      );
    }
  };

  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!colorPopoverOpen) return;
    function handleClickAway(event: MouseEvent) {
      const popover = popoverRef.current;
      if (
        popover &&
        !popover.contains(event.target as Node) &&
        colorAnchorEl &&
        !colorAnchorEl.contains(event.target as Node)
      ) {
        handleColorClose();
      }
    }
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [colorPopoverOpen, colorAnchorEl]);

  const zoomLevel = Math.round((scale / 0.9) * 100);

  return (
    <>
      <div className="toolbar-container">
        <div className="toolbar-content">
          {/* Creation Group */}
          <HighlightedButton 
            onClick={handleAddNoteClick} 
            title={isCreatingNote ? "Creating note..." : (notes.length === 0 ? "Get started - Create New Note" : "Create New Note")}
            className={notes.length === 0 ? 'onboarding' : ''}
            disabled={isCreatingNote}
          >
              {isCreatingNote ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    animation: 'spin 1s linear infinite'
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <PlusIcon />
              )}
          </HighlightedButton>

          <ToolbarButton 
            onClick={handleClearCanvas} 
            title="Clear Canvas"
            disabled={notes.length === 0}
          >
            <ClearIcon />
          </ToolbarButton>

          <div className="toolbar-divider" />

          {/* Zoom Group */}
          <ToolbarButton onClick={handleZoomIn} title="Zoom In">
              <ZoomInIcon />
          </ToolbarButton>
          <button 
              onClick={handleResetView} 
              title="Reset Zoom" 
              className="zoom-level-button"
          >
            {zoomLevel}%
          </button>
          <ToolbarButton onClick={handleZoomOut} title="Zoom Out">
              <ZoomOutIcon />
          </ToolbarButton>

          <div className="toolbar-divider" />

          {/* File & Edit Group */}
          <ToolbarButton onClick={() => setShowSaveTreeModal(true)} title="Save Project">
              <SaveIcon />
          </ToolbarButton>
          <ToolbarButton onClick={() => setShowLoadTreeModal(true)} title="Load Project">
              <FolderIcon />
          </ToolbarButton>
        </div>
      </div>

      {/* Color Popover */}
      {colorPopoverOpen && colorAnchorEl &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            className="color-popover"
            style={{ position: 'absolute', top: colorAnchorEl.getBoundingClientRect().bottom + 8, left: colorAnchorEl.getBoundingClientRect().left, zIndex: 2000 }}
          >
            <div className="color-grid">
              {GLASSMORPHIC_COLORS.map((color) => (
                <button
                  key={color.id}
                  className="color-option"
                  style={{ 
                    background: color.bg,
                    border: `2px solid ${color.border}`,
                    borderRadius: '12px',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    width: '32px',
                    height: '28px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleColorSelect(color.solid)}
                  title={color.name}
                />
              ))}
            </div>
          </div>,
          document.body
        )
      }

      {/* Modals */}
      {showSaveTreeModal && (
        <SaveTreeModal
          isOpen={showSaveTreeModal}
          onClose={() => setShowSaveTreeModal(false)}
          onSave={handleSaveTree}
          yamlContent={getCurrentJsonContent()}
        />
      )}

      {showLoadTreeModal && (
        <LoadTreeModal
          isOpen={showLoadTreeModal}
          onClose={() => setShowLoadTreeModal(false)}
          onLoad={handleLoadTree}
        />
      )}

      {showCanvasNameModal && (
        <CanvasNameModal
          isOpen={showCanvasNameModal}
          onClose={handleCanvasNameCancel}
          onSave={handleCanvasNameSave}
          title="Name Your Canvas"
          placeholder="Enter canvas name..."
        />
      )}

      {showAlertModal && (
        <AlertModal
          isOpen={showAlertModal}
          title={alertModalConfig.title}
          message={alertModalConfig.message}
          buttonText={alertModalConfig.buttonText}
          buttonVariant={alertModalConfig.buttonVariant}
          onClose={() => setShowAlertModal(false)}
          isConfirmation={alertModalConfig.isConfirmation}
          onConfirm={alertModalConfig.onConfirm}
        />
      )}
    </>
  );
};

export default Toolbar;